import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle,
  XCircle,
  Clock,
  FileX,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { format, startOfWeek, addDays, differenceInDays } from 'date-fns';
import type { Timesheet } from '@/types/timesheet';

interface ActionRequiredCardProps {
  onNavigateToTimesheet?: (timesheetId?: number, weekEnding?: Date) => void;
  onViewAllActions?: () => void;
}

interface ActionItem {
  id: string;
  type: 'rejected' | 'missing-hours' | 'overdue' | 'upcoming-deadline';
  title: string;
  description: string;
  urgency: 'high' | 'medium' | 'low';
  count?: number;
  timesheetId?: number;
  date?: Date;
  weekEnding?: Date;
  daysRemaining?: number;
}

export function ActionRequiredCard({ onNavigateToTimesheet, onViewAllActions }: ActionRequiredCardProps) {
  const { timesheets, currentEmployee } = useTimesheetForScreen('homepage');
  const { getScreenMode } = useDataSource();
  const { settings } = useOrganisationSettings('homepage');
  const { summary } = useDashboardSummary(currentEmployee.id, 'homepage');
  const mode = getScreenMode('homepage');
  
  // Get expected hours from organization settings (live mode fetches from API)
  const expectedHoursFromSettings = settings?.workingProfile?.standardHoursPerWeek ?? 40;

  const actionItems = useMemo(() => {
    // Use API data in Live mode, fallback to client-side calculation in Demo mode
    if (mode === 'live' && summary?.actionRequired) {
      return summary.actionRequired.items.map(item => ({
        ...item,
        date: item.date ? new Date(item.date) : undefined,
        weekEnding: item.weekEnding ? new Date(item.weekEnding) : undefined
      }));
    }

    // Fallback to client-side calculation for Demo mode
    const items: ActionItem[] = [];
    const today = new Date();
    // Use organization's configured week start day instead of hardcoded Monday
    const weekStartDay = settings?.weekStartDay ?? 1;
    const currentWeek = startOfWeek(today, { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
    const currentWeekEnd = addDays(currentWeek, 6);

    // Get employee's timesheets
    const employeeTimesheets = timesheets.filter(ts => ts.employeeId === currentEmployee.id);

    // 1. Check for rejected timesheets
    const rejectedTimesheets = employeeTimesheets.filter(ts => ts.status === 'rejected');
    if (rejectedTimesheets.length > 0) {
      rejectedTimesheets.forEach(ts => {
        items.push({
          id: `rejected-${ts.id}`,
          type: 'rejected',
          title: 'Rejected Timesheet',
          description: `Week ending ${format(ts.weekEnding, 'MMM d, yyyy')}${ts.rejectionReason ? `: ${ts.rejectionReason}` : ''}`,
          urgency: 'high',
          timesheetId: ts.id,
          date: ts.rejectedAt,
          weekEnding: ts.weekEnding
        });
      });
    }

    // 2. Check for missing hours in current week
    const currentWeekTimesheet = employeeTimesheets.find(
      ts => format(ts.weekEnding, 'yyyy-MM-dd') === format(currentWeekEnd, 'yyyy-MM-dd')
    );

    if (currentWeekTimesheet) {
      const totalHours = currentWeekTimesheet.entries.reduce((sum, entry) => {
        return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
      }, 0);

      // Use organization settings for expected hours (live mode fetches from API)
      const expectedHours = expectedHoursFromSettings;
      const missingHours = expectedHours - totalHours;

      // Check if it's Thursday or Friday and hours are missing
      const dayOfWeek = today.getDay();
      const isThursdayOrFriday = dayOfWeek === 4 || dayOfWeek === 5;

      if (missingHours > 0 && isThursdayOrFriday && currentWeekTimesheet.status === 'draft') {
        items.push({
          id: 'missing-hours-current',
          type: 'missing-hours',
          title: 'Missing Hours',
          description: `${missingHours.toFixed(1)} hours needed to reach ${expectedHours}h target`,
          urgency: dayOfWeek === 5 ? 'high' : 'medium',
          count: Math.ceil(missingHours),
          timesheetId: currentWeekTimesheet.id,
          date: currentWeekEnd,
          weekEnding: currentWeekEnd
        });
      }
    }

    // Helper function to calculate deadline for a given week using settings from hook
    // This ensures we use API settings in live mode instead of localStorage
    const calculateDeadlineForWeek = (weekStart: Date): Date => {
      const deadline = new Date(weekStart);
      const baseDayOfWeek = deadline.getDay();
      const targetDayOfWeek = settings?.deadline?.deadlineDay ?? 5; // Default to Friday
      const offset = (targetDayOfWeek - baseDayOfWeek + 7) % 7;
      deadline.setDate(deadline.getDate() + offset);
      const [hours, minutes] = (settings?.deadline?.deadlineTime ?? '17:00').split(':').map(Number);
      deadline.setHours(hours, minutes, 0, 0);
      return deadline;
    };

    // Helper function to calculate days until deadline
    const calculateDaysUntilDeadline = (weekStart: Date, referenceDate: Date): number => {
      const deadline = calculateDeadlineForWeek(weekStart);
      const diffMs = deadline.getTime() - referenceDate.getTime();
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    };

    // 3. Check for overdue timesheets (draft status past deadline)
    const overdueTimesheets = employeeTimesheets.filter(ts => {
      if (ts.status !== 'draft') return false;
      
      const weekStart = startOfWeek(ts.weekEnding, { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
      const daysUntilDeadline = calculateDaysUntilDeadline(weekStart, today);
      
      return daysUntilDeadline < 0; // Negative means past deadline
    });

    if (overdueTimesheets.length > 0) {
      overdueTimesheets.forEach(ts => {
        const weekStart = startOfWeek(ts.weekEnding, { weekStartsOn: weekStartDay as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
        const deadline = calculateDeadlineForWeek(weekStart);
        const daysOverdue = Math.abs(differenceInDays(deadline, today));
        
        items.push({
          id: `overdue-${ts.id}`,
          type: 'overdue',
          title: 'Overdue Timesheet',
          description: `Week ending ${format(ts.weekEnding, 'MMM d, yyyy')} - ${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue`,
          urgency: 'high',
          timesheetId: ts.id,
          date: ts.weekEnding,
          weekEnding: ts.weekEnding
        });
      });
    }

    // PHASE 2: 4. Check for upcoming deadlines (current week if draft and approaching deadline)
    if (currentWeekTimesheet && currentWeekTimesheet.status === 'draft') {
      const daysUntilDeadline = calculateDaysUntilDeadline(currentWeek, today);
      const deadline = calculateDeadlineForWeek(currentWeek);
      
      // Show upcoming deadline if it's within 3 days and not already overdue
      if (daysUntilDeadline >= 0 && daysUntilDeadline <= 3) {
        const totalHours = currentWeekTimesheet.entries.reduce((sum, entry) => {
          return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
        }, 0);
        
        // Use organization settings for expected hours (live mode fetches from API)
        const expectedHours = expectedHoursFromSettings;
        const completionPercentage = (totalHours / expectedHours) * 100;
        
        // Determine urgency based on days remaining and completion
        let urgency: 'high' | 'medium' | 'low' = 'low';
        if (daysUntilDeadline === 0) {
          urgency = 'high';
        } else if (daysUntilDeadline === 1 && completionPercentage < 75) {
          urgency = 'high';
        } else if (daysUntilDeadline <= 2 && completionPercentage < 50) {
          urgency = 'medium';
        } else if (daysUntilDeadline <= 3) {
          urgency = 'low';
        }
        
        items.push({
          id: 'upcoming-deadline-current',
          type: 'upcoming-deadline',
          title: 'Upcoming Deadline',
          description: daysUntilDeadline === 0 
            ? `Due today at 5:00 PM - ${completionPercentage.toFixed(0)}% complete`
            : daysUntilDeadline === 1
            ? `Due tomorrow - ${completionPercentage.toFixed(0)}% complete`
            : `Due in ${daysUntilDeadline} days - ${completionPercentage.toFixed(0)}% complete`,
          urgency,
          timesheetId: currentWeekTimesheet.id,
          date: deadline,
          weekEnding: currentWeekEnd,
          daysRemaining: daysUntilDeadline
        });
      }
    }

    // Sort by urgency (high first) and then by date (most recent first)
    return items.sort((a, b) => {
      const urgencyOrder = { high: 0, medium: 1, low: 2 };
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      }
      if (a.date && b.date) {
        return b.date.getTime() - a.date.getTime();
      }
      return 0;
    });
  }, [mode, summary, timesheets, currentEmployee, expectedHoursFromSettings, settings]);

  // Configuration for action item types
  const actionConfig = {
    rejected: {
      icon: XCircle,
      accentClass: 'accent-danger',
      badgeVariant: 'destructive' as const
    },
    'missing-hours': {
      icon: Clock,
      accentClass: 'accent-warning',
      badgeVariant: 'secondary' as const
    },
    overdue: {
      icon: AlertTriangle,
      accentClass: 'accent-danger',
      badgeVariant: 'destructive' as const
    },
    'upcoming-deadline': {
      icon: Clock,
      accentClass: 'accent-info',
      badgeVariant: 'default' as const
    }
  };

  const totalActions = actionItems.length;
  const highPriorityCount = actionItems.filter(item => item.urgency === 'high').length;

  // Display up to 3 items in the card
  const displayedItems = actionItems.slice(0, 3);
  const hasMoreItems = actionItems.length > 3;

  if (totalActions === 0) {
    return (
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-accent-success-50 dark:bg-accent-success-900/20">
                  <AlertCircle className="h-4 w-4 text-accent-success-700 dark:text-accent-success-400" />
                </div>
                <span className="text-accent-success-900 dark:text-accent-success-100">Action Required</span>
              </CardTitle>
              <p className="text-xs text-muted-foreground ml-9">
                No urgent actions needed
              </p>
            </div>
            <Badge variant="secondary" className="flex items-center gap-1 bg-accent-success-50 text-accent-success-900 dark:bg-accent-success-900/20 dark:text-accent-success-100 border-accent-success-200 dark:border-accent-success-800">
              All Clear
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          <div className="accent-success flex items-center justify-center p-8 text-center">
            <div className="space-y-2">
              <div className="flex justify-center">
                <div className="p-3 rounded-full bg-[var(--accent-bg-outline)]">
                  <AlertCircle className="h-6 w-6 text-[var(--accent-text-on-outline)]" />
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                You're all caught up!
              </p>
              <p className="text-xs text-muted-foreground">
                No pending actions at this time
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-accent-warning-50 dark:bg-accent-warning-900/20">
                <AlertTriangle className="h-4 w-4 text-accent-warning-700 dark:text-accent-warning-400" />
              </div>
              <span className="text-accent-warning-900 dark:text-accent-warning-100">Action Required</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground ml-9">
              {totalActions} item{totalActions > 1 ? 's' : ''} need{totalActions === 1 ? 's' : ''} your attention
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Action Items List */}
        <div className="space-y-2">
          {displayedItems.map((item) => {
            const config = actionConfig[item.type];
            const Icon = config.icon;

            return (
              <div
                key={item.id}
                className={`${config.accentClass} flex items-start gap-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200 cursor-pointer hover:shadow-sm transition-shadow`}
                onClick={() => onNavigateToTimesheet?.(item.timesheetId, item.weekEnding)}
              >
                <div className={`p-1.5 rounded-md ${item.urgency === 'high' ? 'bg-warning-100' : 'bg-teal-100'}`}>
                  <Icon className={`h-4 w-4 ${item.urgency === 'high' ? 'text-warning-600' : 'text-teal-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">
                          {item.title}
                        </p>
                        {item.urgency === 'high' && (
                          <Badge variant="outline" className="text-xs bg-warning-100 text-warning-700 border-warning-300">
                            Urgent
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        {hasMoreItems && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onViewAllActions}
          >
            View All {totalActions} Actions
            <ChevronRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        )}

        {/* Quick Action Summary */}
        {!hasMoreItems && totalActions > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground text-center">
              Click any item to take action
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
